/*
 * MetricTile — a single live metric, with optional sparkline and a link back
 * to its source (the /v1/metrics line it was parsed from). A metric the
 * console cannot read renders as a loud unknown, never a fabricated zero.
 */

import type { MetricPoint } from "../api/types";

interface MetricTileProps {
  label: string;
  /** The parsed value, or null/undefined when unreadable. */
  value: number | null | undefined;
  /** Unit suffix, e.g. "ms", "req". */
  unit?: string;
  /** Semantic accent var — colour by meaning, e.g. "var(--ember)". */
  accent?: string;
  /** Optional rolling series for a sparkline. */
  series?: MetricPoint[];
  /** The Prometheus series name, shown as the provenance footer. */
  source?: string;
  /** Loud when the value represents a failure surface (replays, dead letters). */
  loud?: boolean;
  /** Inspect the source line / proof. */
  onInspect?: () => void;
}

export function MetricTile({
  label,
  value,
  unit,
  accent = "var(--fg)",
  series,
  source,
  loud,
  onInspect,
}: MetricTileProps) {
  const unreadable = value === null || value === undefined || !Number.isFinite(value);
  const display = unreadable
    ? "—"
    : Math.abs(value!) >= 1000
      ? value!.toLocaleString()
      : String(value);

  // A loud security-signal tile is ALARMING only when its value is non-zero.
  // The alarm is conveyed by more than colour (WCAG 1.4.1): role="alert",
  // an aria-label that states the action, and a visible glyph + "NON-ZERO" chip.
  const alarm = Boolean(loud) && !unreadable && value! > 0;

  return (
    <div
      className="panel"
      role={alarm ? "alert" : undefined}
      aria-label={alarm ? `${label}: ${display}${unit ? ` ${unit}` : ""} — investigate` : undefined}
      style={{
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        borderColor: alarm ? "rgba(var(--red-rgb),0.4)" : "var(--hair)",
        background: alarm ? "var(--red-dim)" : "var(--bg-2)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: "11px",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-faint)",
          }}
        >
          {label}
        </span>
        {alarm && (
          <span
            className="mono"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "9.5px",
              letterSpacing: "0.06em",
              padding: "1px 5px",
              borderRadius: "999px",
              color: "var(--red)",
              border: "1px solid var(--red)",
              fontWeight: 600,
            }}
          >
            <span aria-hidden="true">▲</span> NON-ZERO
          </span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "28px",
            color: unreadable ? "var(--fg-faint)" : alarm ? "var(--red)" : accent,
            fontWeight: 500,
          }}
          title={unreadable ? "metric not readable from /v1/metrics" : undefined}
        >
          {display}
        </span>
        {unit && !unreadable && (
          <span className="mono" style={{ fontSize: "12px", color: "var(--fg-faint)" }}>
            {unit}
          </span>
        )}
      </div>

      {series && series.length > 1 && (
        <Sparkline points={series} accent={alarm ? "var(--red)" : accent} />
      )}

      {source && (
        <button
          type="button"
          onClick={onInspect}
          disabled={!onInspect}
          style={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            padding: 0,
            marginTop: "2px",
            fontFamily: "var(--font-mono)",
            fontSize: "10.5px",
            color: "var(--fg-faint)",
            cursor: onInspect ? "pointer" : "default",
            textAlign: "left",
            overflowWrap: "anywhere",
          }}
          title={onInspect ? "Inspect source line" : source}
        >
          {source}
          {onInspect ? " ↗" : ""}
        </button>
      )}
    </div>
  );
}

/** Minimal dependency-free SVG sparkline. */
function Sparkline({ points, accent }: { points: MetricPoint[]; accent: string }) {
  const w = 200;
  const h = 36;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * step;
      const y = h - ((p.value - min) / span) * (h - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      height={h}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d={d} fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}
